import React, { useState } from "react";
import "./Home.css";
import { db } from "../firebase";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { toast } from "react-toastify";

const generateTicketId = (serviceType) => {
  let prefix = "B";
  if (serviceType) {
    prefix = serviceType === "General Service" ? "S" : serviceType.charAt(0).toUpperCase();
  }
  const date = new Date();
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  const random = Math.floor(1000 + Math.random() * 9000); // 4 digit random number
  return `${prefix}-${yyyy}${mm}${dd}-${random}`;
};

export default function Home() {
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    mobile: "",
    serviceType: "General Service",
    address: "",
    date: "",
  });
  const [loading, setLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // mobile validation
    if (!/^\d{10}$/.test(formData.mobile)) {
      toast.error("Please enter a valid 10-digit mobile number.");
      setLoading(false);
      return;
    }

    try {
      const ticketId = generateTicketId(formData.serviceType);
      await addDoc(collection(db, "bookings"), {
        ...formData,
        ticketId,
        status: "Pending",
        createdAt: serverTimestamp(),
      });
      toast.success(`Booking Request Sent Successfully! Ticket ID: ${ticketId}`, { autoClose: 8000 });
      setShowModal(false);
      setFormData({
        name: "",
        mobile: "",
        serviceType: "General Service",
        address: "",
        date: "",
      });
    } catch (error) {
      console.error("Error booking service:", error);
      toast.error("Failed to book service. Please try again.");
    }
    setLoading(false);
  };

  return (
    <div className="home-container">
      {/* Hero Section */}
      <section className="hero-section text-center">
        <div className="hero-overlay"></div>
        <div className="container hero-content">
          <h1 className="hero-title">Stay Cool. Stay Comfortable.</h1>
          <p className="hero-subtitle">
           Expert AC repair, installation & maintenance services to keep your home and office perfectly cool — even in peak summer.
          </p>
          <div className="d-flex justify-content-center gap-3 mt-4">
            <button
              className="btn btn-custom-primary btn-lg d-inline-flex align-items-center"
              onClick={() => setShowModal(true)}
              style={{ borderRadius: "50px", padding: "12px 30px" }}
            >
              <i className="bi bi-calendar-check me-2"></i> Book Now
            </button>
            <a
              href="https://wa.me/919688317649?text=Hello,%20I%20need%20AC%20service."
              className="btn btn-success btn-lg d-inline-flex align-items-center"
              target="_blank"
              rel="noopener noreferrer"
              style={{ borderRadius: "50px", padding: "12px 30px" }}
            >
              <i className="bi bi-whatsapp me-2"></i> WhatsApp Us
            </a>
          </div>
        </div>
      </section>

      {/* Service Rates */}
      <section id="services" className="py-5 bg-white">
        <div className="container">
          <h2 className="text-center mb-5 fw-bold" style={{ color: "#1e3c72" }}>
            Our Service Rates
          </h2>
          <div className="row g-4">
            <div className="col-md-4">
              <div className="glass-card text-center">
                <i className="bi bi-tools service-icon"></i>
                <h4 className="fw-bold mt-3">General Service</h4>
                <p className="text-muted">Complete cleaning and inspection</p>
                <div className="price-tag">From ₹550</div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="glass-card text-center">
                <i className="bi bi-box-seam service-icon"></i>
                <h4 className="fw-bold mt-3">Installation</h4>
                <p className="text-muted">Professional AC installation</p>
                <div className="price-tag">From ₹1500</div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="glass-card text-center">
                <i className="bi bi-speedometer2 service-icon"></i>
                <h4 className="fw-bold mt-3">Gas Filling</h4>
                <p className="text-muted">Comprehensive gas fill & leak test</p>
                <div className="price-tag">From ₹1800</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="why-us-section">
        <div className="container">
          <h2 className="text-center mb-5 fw-bold" style={{ color: "#1e3c72" }}>
            Why Choose Us
          </h2>
          <div className="row">
            <div className="col-md-4 feature-box">
              <i className="bi bi-shield-check feature-icon"></i>
              <h5>Reliable Professionals</h5>
              <p className="text-muted">
                Trustworthy experts for maintenance and repairs.
              </p>
            </div>
            <div className="col-md-4 feature-box">
              <i className="bi bi-gear-wide-connected feature-icon"></i>
              <h5>Tailored Solutions</h5>
              <p className="text-muted">
                Customized services to fit your specific needs.
              </p>
            </div>
            <div className="col-md-4 feature-box">
              <i className="bi bi-star-fill feature-icon"></i>
              <h5>Satisfaction Guaranteed</h5>
              <p className="text-muted">
                Quality parts and workmanship, every time.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="bg-dark text-white py-5">
        <div className="container">
          <h2 className="text-center mb-5">Contact Us</h2>
          <div className="row text-center">
            <div className="col-md-4 mb-4">
              <i className="bi bi-geo-alt-fill fs-2 mb-3 text-primary"></i>
              <h5>Location</h5>
              <p className="small">
                No 11 Santhosh Nagar, Pattunoolchathiram, Sriperumbudur - 602105
              </p>
            </div>
            <div className="col-md-4 mb-4">
              <i className="bi bi-clock-fill fs-2 mb-3 text-primary"></i>
              <h5>Hours</h5>
              <p className="small">Mon-Fri: 9am - 5pm</p>
            </div>
            <div className="col-md-4 mb-4">
              <i className="bi bi-telephone-fill fs-2 mb-3 text-primary"></i>
              <h5>Contact</h5>
              <p className="small">
                <a href="tel:09688317649" className="text-white text-decoration-none">
                  09688317649
                </a>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Booking Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content-custom" onClick={(e) => e.stopPropagation()}>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h4 className="fw-bold m-0">Book a Service</h4>
              <button
                className="btn-close"
                onClick={() => setShowModal(false)}
              ></button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="mb-3">
                <label className="form-label">Name <span className="text-danger">*</span></label>
                <input
                  type="text"
                  name="name"
                  className="form-control"
                  required
                  value={formData.name}
                  onChange={handleInputChange}
                />
              </div>
              <div className="mb-3">
                <label className="form-label">Mobile Number <span className="text-danger">*</span></label>
                <input
                  type="tel"
                  name="mobile"
                  className="form-control"
                  required
                  pattern="[0-9]{10}"
                  maxLength="10"
                  placeholder="10-digit mobile number"
                  value={formData.mobile}
                  onChange={handleInputChange}
                />
              </div>
              <div className="mb-3">
                <label className="form-label">Service Type <span className="text-danger">*</span></label>
                <select
                  name="serviceType"
                  className="form-select"
                  value={formData.serviceType}
                  onChange={handleInputChange}
                >
                  <option>General Service</option>
                  <option>Installation</option>
                  <option>Gas Filling</option>
                  <option>Repair / Breakdown</option>
                  <option>Other</option>
                </select>
              </div>
              <div className="mb-3">
                <label className="form-label">Address <span className="text-danger">*</span></label>
                <textarea
                  name="address"
                  className="form-control"
                  rows="2"
                  required
                  value={formData.address}
                  onChange={handleInputChange}
                ></textarea>
              </div>
              <div className="mb-3">
                <label className="form-label">Preferred Date <span className="text-danger">*</span></label>
                <input
                  type="date"
                  name="date"
                  className="form-control"
                  required
                  min={new Date().toISOString().split("T")[0]}
                  value={formData.date}
                  onChange={handleInputChange}
                />
              </div>
              <div className="d-grid">
                <button
                  type="submit"
                  className="btn btn-custom-primary"
                  disabled={loading}
                >
                  {loading ? "Booking..." : "Confirm Booking"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}